import {
  Box,
  Checkbox,
  FormControlLabel,
  Modal,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/components/company/batchCreate/companyBatchEmailModal.module.css";
import { Controller, useForm } from "react-hook-form";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
import { api } from "@/helper/api";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { useConfirmationModal } from "@/context/confirmationModalContext";

interface CompanyBatchEmailModalProps {
  modalOpen: boolean;
  onClose: () => void;
  selectedTableRows: any[];
  handleSubmitFormData: (data: any) => void;
}

type CompanyBatchEmailFormValues = {
  is_exclusion_list: boolean;
  include_children: boolean;
  parents_only: boolean;
  mail_template: any;
  additional_tags: any;
  send_to_customers: boolean;
  include_unregistered: boolean;
};

const CompanyBatchEmailModal: FC<CompanyBatchEmailModalProps> = ({
  modalOpen,
  onClose,
  selectedTableRows,
  handleSubmitFormData,
}) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CompanyBatchEmailFormValues>({
    shouldFocusError: false,
    defaultValues: {
      is_exclusion_list: false,
      include_children: false,
      parents_only: false,
      mail_template: "",
      additional_tags: "",
      send_to_customers: false,
      include_unregistered: false,
    },
  });
  const parentsOnlySelected = watch("parents_only");
  const includeChildrenSelected = watch("include_children");
  const isExclusionList = watch("is_exclusion_list");
  const mailTemplateSelected = watch("mail_template");
  const sendToCustomersSelected = watch("send_to_customers");
  const includeUnregisteredSelected = watch("include_unregistered");
  const recipientGroup = sendToCustomersSelected ? "customers" : "managers";
  const { showConfirmationModalAsync } = useConfirmationModal();
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<CompanyBatchEmailFormValues>({
      fieldOrder: ["mail_template"],
    });

  const [additionalTags, setAdditionalTags] = useState<
    Array<{ key: string; value: string | null }>
  >([]);

  useEffect(() => {
    if (modalOpen) {
      reset();
    }
  }, [modalOpen, reset]);

  useEffect(() => {
    if (mailTemplateSelected && mailTemplateSelected.value) {
      fetchAvailableTags();
    } else {
      setAdditionalTags([]);
    }
  }, [mailTemplateSelected]);
  const getTranslationKey = () => {
    const baseKey = getRecipientTranslationKey();
    return includeUnregisteredSelected
      ? `${baseKey}_include_unregistered`
      : baseKey;
  };

  const getRecipientTranslationKey = () => {
    const count = selectedTableRows.length;

    if (count === 0) {
      if (parentsOnlySelected)
        return sendToCustomersSelected
          ? "send_email_to_customers_of_all_companies_parents_only"
          : "no_companies_selected_send_to_all_parents_only";
      return sendToCustomersSelected
        ? "send_email_to_customers_of_all_companies"
        : "no_companies_selected_send_to_all";
    }

    if (isExclusionList) {
      if (parentsOnlySelected) {
        return sendToCustomersSelected
          ? "send_email_to_customers_of_all_companies_except_selected_parents_only"
          : "send_email_to_all_companies_except_selected_parents_only";
      }
      if (includeChildrenSelected) {
        return sendToCustomersSelected
          ? "send_email_to_customers_of_all_companies_except_selected_include_children"
          : "send_email_to_all_companies_except_selected_include_children";
      }
      return sendToCustomersSelected
        ? "send_email_to_customers_of_all_companies_except_selected_companies"
        : "send_email_to_all_companies_except_selected_companies";
    }

    if (parentsOnlySelected && !isExclusionList) {
      return sendToCustomersSelected
        ? "send_email_to_customers_of_selected_companies_parents_only"
        : "send_email_to_selected_companies_parents_only";
    }

    if (includeChildrenSelected) {
      return sendToCustomersSelected
        ? "send_email_to_customers_of_selected_companies_include_children"
        : "send_email_to_all_selected_companies_include_children";
    }

    return sendToCustomersSelected
      ? "send_email_to_customers_of_selected_companies"
      : "send_email_to_selected_companies";
  };

  const privacyHint = (
    <div className={`${styles.hintCard} ${styles.hintCardWarning}`}>
      <span className={styles.hintIcon}>
        <ShieldOutlinedIcon />
      </span>
      <p className={styles.hintText}>
        {t("include_unregistered_privacy_hint")}
      </p>
    </div>
  );

  const confirmIncludeUnregistered = async (
    checked: boolean,
    onChange: (value: boolean) => void
  ) => {
    if (!checked) {
      onChange(false);
      return;
    }
    const isConfirmed = await showConfirmationModalAsync(
      <div className={styles.confirmContainer}>
        <p className={styles.confirmText}>
          {t(`include_unregistered_confirm_${recipientGroup}`)}
        </p>
        {privacyHint}
      </div>
    );
    onChange(isConfirmed);
  };

  const onSubmit = (data: any) => {
    const tempCompanySubjectSet =
      selectedTableRows.length > 0
        ? selectedTableRows
            .map((row: any) => row.subject?.id || row.subject_id)
            .filter(Boolean)
        : [];

    const additionalTagsObj: Record<string, string> = {};

    if (data.additional_tags.length > 0) {
      data.additional_tags.forEach((tag: any) => {
        if (tag.key) {
          const trimmedValue =
            typeof tag.value === "string" ? tag.value.trim() : tag.value || "";
          if (trimmedValue !== "") {
            additionalTagsObj[tag.key] = trimmedValue;
          }
        }
      });
    }

    const additionalTagsToSubmit =
      Object.keys(additionalTagsObj).length > 0 ? additionalTagsObj : null;

    const submitData: any = {
      mail_template: data.mail_template.value,
      company_subject_set: tempCompanySubjectSet,
      parents_only: data.parents_only,
      is_exclusion_list: data.is_exclusion_list,
      include_children: data.include_children,
      send_to_customers: data.send_to_customers,
      additionalTags: additionalTagsToSubmit,
      ...(data.include_unregistered ? { include_unregistered: true } : {}),
    };

    handleSubmitFormData(submitData);
  };

  const extractVariablesFromMailBody = (mailBody: string): string[] => {
    const regex = /\{\{\s*([^}|]+)(?:\|.*?)?\s*\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = regex.exec(mailBody)) !== null) {
      const variableName = match[1].trim();
      if (variableName && !variables.includes(variableName)) {
        variables.push(variableName);
      }
    }

    return variables;
  };

  const fetchAvailableTags = async () => {
    try {
      const additionalUrlParameters: any = {
        name: mailTemplateSelected.value,
      };

      api.genericApiRequest({
        method: "get",
        entity: "mailTemplate",
        parametersToRender: {
          depth: "0",
          additionalUrlParameters: additionalUrlParameters,
        },
        successHandler: (res: any) => {
          const templates = res.data?.results || [];
          if (templates.length > 0) {
            const template = templates[0];
            const templateType = template.type;
            const mailBody = template.mail_body || "";

            if (templateType) {
              const additionalUrlParameters: any = {};
              additionalUrlParameters["type"] = templateType;
              const additionalRouteParts: any = {};
              additionalRouteParts["available_tags"] = "/";

              api.genericApiRequest({
                method: "get",
                entity: "mailTemplate",
                additionalRouteParts: additionalRouteParts,
                parametersToRender: {
                  depth: "0",
                  additionalUrlParameters: additionalUrlParameters,
                },
                successHandler: (tagsRes: any) => {
                  const availableTagsFromApi = tagsRes.data?.results || [];

                  const variablesInMailBody =
                    extractVariablesFromMailBody(mailBody);

                  const additionalTagsNeeded = variablesInMailBody.filter(
                    (variable: string) =>
                      !availableTagsFromApi.includes(variable)
                  );

                  if (additionalTagsNeeded.length > 0) {
                    const newTags = additionalTagsNeeded.map((tag: string) => ({
                      key: tag,
                      value: null,
                    }));
                    setAdditionalTags(newTags);
                  } else {
                    setAdditionalTags([]);
                  }
                },
                failHandler: (error: any) => {
                  console.error("Error fetching available tags:", error);
                  setAdditionalTags([]);
                },
              });
            } else {
              setAdditionalTags([]);
            }
          } else {
            setAdditionalTags([]);
          }
        },
        failHandler: (error: any) => {
          console.error("Error fetching template:", error);
          setAdditionalTags([]);
        },
      });
    } catch (error: any) {
      console.error("Error in fetchAvailableTags:", error);
      setAdditionalTags([]);
    }
  };

  const handleAdditionalTagsValueBlur = (
    index: number,
    target: HTMLInputElement | HTMLTextAreaElement,
    onChange: (data: any) => void
  ) => {
    const rawValue = target.value;
    const trimmedValue = rawValue.trim();
    const sanitizedValue = trimmedValue === "" ? null : trimmedValue;

    target.value = sanitizedValue ?? "";

    const newTags = [...additionalTags];
    newTags[index] = { ...newTags[index], value: sanitizedValue };
    setAdditionalTags(newTags);
    onChange(newTags);
  };

  const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    entity: "mailTemplate",
  });

  useModalDismiss(modalOpen, onClose);

  return (
    <Modal open={modalOpen} onClose={onClose} disableAutoFocus>
      <Box className={styles.modalContentBoxWrapper}>
        <Typography variant="h6" component="h2" gutterBottom align="center">
          {t("send_email")}
        </Typography>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit, (errors) => {
            focusFirstInvalidField(errors);
          })}
        >
          <Box className={styles.formContent}>
            <Controller
              name="mail_template"
              control={control}
              rules={{ required: t("field_required") as string }}
              render={({ field: { onChange, value, name } }) => (
                <div ref={registerValidationTarget("mail_template")}>
                  <FormFieldLabel
                    label={t("mail_template")}
                    htmlFor={name}
                    mandatory
                  />
                  <SubjectSearchSelect
                    value={value}
                    name={name}
                    loadOptions={(input: any) =>
                      fetchSubjects(input, {
                        filterParameter: {
                          ordering: "name",
                          name__icontains: input,
                        },
                        mapResponseToOptions: (data) =>
                          data.results.map((item: any) => ({
                            value: item.name || item.id,
                            label: `${item.name}`,
                          })),
                      })
                    }
                    cacheOptions={false}
                    onChange={onChange}
                    isClearable={true}
                    placeholder={t("search_placeholder")}
                  />
                </div>
              )}
            />
            {errors.mail_template && (
              <Typography color="error" variant="caption">
                {String(errors.mail_template.message)}
              </Typography>
            )}
            {mailTemplateSelected && additionalTags.length > 0 && (
              <Box className={styles.additionalTagsContainer}>
                <Box className={styles.additionalTagsHeaderContainer}>
                  <Typography variant="body1">
                    {t("additional_data")}
                  </Typography>
                </Box>
                <Box className={styles.additionalTagsContentContainer}>
                  {additionalTags.map((tag, index) => (
                    <Box
                      key={index}
                      className={styles.additionalTagsContentItem}
                    >
                      <TextAreaWithRef
                        rows={1}
                        value={tag.key}
                        disabled
                        hideLabel
                        wrapperClassname={styles.additionalTagsContentItemKey}
                        fieldStyle={{
                          WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                          color: "rgba(0, 0, 0, 0.87)",
                          cursor: "not-allowed",
                          resize: "vertical",
                        }}
                      />
                      <Controller
                        name="additional_tags"
                        control={control}
                        render={({ field }) => (
                          <>
                            <TextAreaWithRef
                              rows={1}
                              placeholder={t("value")}
                              defaultValue={tag.value || ""}
                              hideLabel
                              onBlur={(e) =>
                                handleAdditionalTagsValueBlur(
                                  index,
                                  e.target,
                                  field.onChange
                                )
                              }
                              wrapperClassname={
                                styles.additionalTagsContentItemValue
                              }
                              fieldStyle={{ resize: "vertical" }}
                            />
                          </>
                        )}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
            <Box className={styles.checkboxWrapper}>
              <Controller
                name="is_exclusion_list"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("is_exclusion_list")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          disabled={selectedTableRows.length === 0}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <Controller
                name="include_children"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("include_child_companies")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={
                            parentsOnlySelected ||
                            selectedTableRows.length === 0
                          }
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <Controller
                name="parents_only"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("only_parent_companies")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={includeChildrenSelected}
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <Controller
                name="send_to_customers"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("send_to_customers")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          // disabled={includeChildrenSelected}
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <Controller
                name="include_unregistered"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t(`include_unregistered_${recipientGroup}`)}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            confirmIncludeUnregistered(
                              event.target.checked,
                              field.onChange
                            )
                          }
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
            </Box>
            <div className={styles.hintCard}>
              <span className={styles.hintIcon}>
                <InfoOutlinedIcon />
              </span>
              <p className={styles.hintText}>
                {t(getTranslationKey(), { count: selectedTableRows.length })}
              </p>
            </div>
            {includeUnregisteredSelected && privacyHint}
            <Box className={styles.buttonWrapper}>
              <GenericButton variant="outlined" onClick={onClose}>
                {t("cancel")}
              </GenericButton>
              <GenericButton variant="contained" type="submit">
                {t("save")}
              </GenericButton>
            </Box>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
export default CompanyBatchEmailModal;
